import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RefreshCw, ScrollText } from 'lucide-react';
import { Button, Select, Switch, TextField } from '@nyce/admin-kit/ui';
import { EmptyState, ErrorBox, Loading, Page, Tabs, useAsync, useNotice } from '../../components/common';
import type { LogEntry } from '../../api/logs';
import { logsLink } from '../../lib/logFilters';
import { LogTable } from '../logs/LogTable';
import { pods, type Pod } from '../../api/pods';
import { errorMessage } from '../../lib/errors';
import { useParam } from '../statistics/shared';

const TAILS = [100, 500, 1000, 5000];

type View = 'entries' | 'raw';
const LEVELS: { value: string; label: string; min: number }[] = [
  { value: 'all', label: 'All levels', min: 0 },
  { value: 'warning', label: 'Warnings and up', min: 400 },
  { value: 'error', label: 'Errors and up', min: 500 },
];
const RANK: Record<string, number> = {
  DEFAULT: 0,
  DEBUG: 100,
  INFO: 200,
  NOTICE: 300,
  WARNING: 400,
  ERROR: 500,
  CRITICAL: 600,
  ALERT: 700,
  EMERGENCY: 800,
};

/** A pod's log page, from the Pods page. */
export const podLogsPath = (env: string, pod: Pick<Pod, 'namespace' | 'name'>) =>
  `/pods/logs?${new URLSearchParams({ env, ns: pod.namespace, pod: pod.name })}`;

/**
 * `/pods/logs?env&ns&pod`: a pod's live log from the k8s API, shown like the Logs page (time, severity, message; a
 * row opens the entry under it) or raw. Level and text filters work on the loaded lines; "Open in Logs" continues in
 * Cloud Logging with the same pod, container and time window for real filtering, history and other replicas. The
 * container, view and filters are in the URL, so the view can be shared.
 */
export function PodLogsPage() {
  const [env] = useParam('env', '');
  const [ns] = useParam('ns', '');
  const [name] = useParam('pod', '');
  // The pod's containers come from the pod list; a pod that is gone keeps its page (its log may still be readable).
  const list = useAsync(() => (env ? pods.list(env) : Promise.resolve(null)), [env]);
  const pod: Pod | null =
    list.data?.pods.find((p) => p.namespace === ns && p.name === name) ??
    (list.data || list.error ? ({ namespace: ns, name, containers: [] } as unknown as Pod) : null);
  const back = `/pods?${new URLSearchParams({ env })}`;
  if (!env || !ns || !name)
    return (
      <Page title="Pod logs">
        <EmptyState card title="Open a pod's logs from the Pods page." />
      </Page>
    );
  return (
    <Page
      title={
        <>
          <Link to={back} className="nyk-back">
            Pods
          </Link>
          <span className="nyk-crumb-sep"> / </span>
          Logs · {name}
        </>
      }
      description={<span className="nyk-mono">{ns}</span>}
    >
      <Loading loading={list.loading && !list.data} />
      {pod && <PodLogs key={`${env}/${ns}/${name}`} environmentId={env} pod={pod} />}
    </Page>
  );
}

function PodLogs({ environmentId, pod }: { environmentId: string; pod: Pod }) {
  const navigate = useNavigate();
  const notice = useNotice();
  const [container, setContainer] = useParam('container', pod.containers[0]?.name ?? '');
  const [tailParam, setTailParam] = useParam('tail', '500');
  const tail = TAILS.includes(Number(tailParam)) ? Number(tailParam) : 500;
  const setTail = (n: number) => setTailParam(String(n));
  const [prev, setPrev] = useParam('previous', '');
  const previous = prev === '1';
  const setPrevious = (v: boolean) => setPrev(v ? '1' : '');
  const [view, setView] = useParam<View>('view', 'entries');
  const [level, setLevel] = useParam('level', 'all');
  const [filter, setFilter] = useParam('q', '');
  const [entries, setEntries] = useState<LogEntry[] | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pre = useRef<HTMLPreElement>(null);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const id = ++seq.current;
    setLoading(true);
    setError(null);
    const q = {
      environmentId,
      namespace: pod.namespace,
      pod: pod.name,
      container: container || undefined,
      tail,
      previous,
    };
    try {
      if (view === 'raw') {
        const t = await pods.logs(q);
        if (id === seq.current) setText(t);
      } else {
        const r = await pods.logEntries(q);
        if (id === seq.current) setEntries(r.entries);
      }
    } catch (e) {
      if (id === seq.current) {
        setText(null);
        setEntries(null);
        setError(errorMessage(e));
      }
    } finally {
      if (id === seq.current) setLoading(false);
    }
  }, [environmentId, pod.namespace, pod.name, container, tail, previous, view]);

  useEffect(() => {
    void load();
  }, [load]);
  useLayoutEffect(() => {
    const el = pre.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [text]);

  const min = LEVELS.find((l) => l.value === level)?.min ?? 0;
  const needle = filter.trim().toLowerCase();
  const shown = useMemo(
    () =>
      (entries ?? []).filter(
        (e) => (RANK[e.severity] ?? 0) >= min && (!needle || e.message.toLowerCase().includes(needle))
      ),
    [entries, min, needle]
  );

  const openInLogs = () => {
    const stamps = (entries ?? []).map((e) => new Date(e.timestamp).getTime()).filter(Number.isFinite);
    const since = stamps.length ? new Date(Math.min(...stamps)) : undefined;
    navigate(logsLink(environmentId, pod.namespace, pod.name, { container: container || undefined, since }));
  };

  return (
    <div className="nyk-card">
      <div className="oph-pod-logs">
        <div className="nyk-inline oph-pod-logs-bar oph-pod-logs-actions">
          <Button size="sm" variant="ghost" icon={<ScrollText size={14} />} onClick={openInLogs}>
            Open in Logs
          </Button>
          <Button
            size="sm"
            variant="ghost"
            icon={<RefreshCw size={14} />}
            loading={loading}
            onClick={() => void load()}
          >
            Refresh
          </Button>
        </div>
        <div className="nyk-inline oph-pod-logs-bar">
          <Tabs
            value={view}
            onChange={setView}
            options={[
              { value: 'entries', label: 'Formatted' },
              { value: 'raw', label: 'Raw' },
            ]}
          />
          {pod.containers.length > 1 && (
            <Select
              aria-label="Container"
              value={container}
              onChange={setContainer}
              options={pod.containers.map((c) => ({ value: c.name, label: c.name }))}
            />
          )}
          <Select
            aria-label="Tail lines"
            value={String(tail)}
            onChange={(v) => setTail(Number(v))}
            options={TAILS.map((n) => ({ value: String(n), label: `last ${n} lines` }))}
          />
          <Switch checked={previous} onChange={setPrevious} label="Previous instance" />
        </div>
        {view === 'entries' && (
          <div className="nyk-inline oph-pod-logs-bar">
            <Select
              aria-label="Level"
              value={level}
              onChange={setLevel}
              options={LEVELS.map((l) => ({ value: l.value, label: l.label }))}
            />
            <TextField
              aria-label="Filter messages"
              placeholder="Filter loaded lines…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            {entries && (
              <span className="oph-muted">
                {shown.length === entries.length
                  ? `${entries.length} entries`
                  : `${shown.length} of ${entries.length} entries`}
              </span>
            )}
          </div>
        )}
        <ErrorBox error={error} />
        {loading && (view === 'raw' ? text === null : entries === null) && <Loading loading />}
        {view === 'raw' && text !== null && (
          <pre ref={pre} className="oph-term" data-testid="pod-log-text">
            {text || '(no output)'}
          </pre>
        )}
        {view === 'entries' &&
          entries !== null &&
          (shown.length ? (
            <LogTable entries={shown} onCopied={notice.success} columns={[]} />
          ) : (
            <EmptyState
              title={entries.length ? 'No lines match' : 'No output'}
              hint={entries.length ? 'Relax the level or text filter.' : undefined}
            />
          ))}
      </div>
      {notice.element}
    </div>
  );
}
